import React, {
  useEffect,
  useState,
  useRef,
} from 'react'
import { compose } from 'recompose'
import _last from 'lodash/last'
import _get from 'lodash/get'
import { fromJS, List } from 'immutable'
import { connect as connectRedux } from 'react-redux'
import { useLazyQuery } from '@apollo/client'
import { getBoundActions } from '../../actions/index'
import { URL_SHORT_VIDEOS } from '../../services/url/constants'
import { GET_CLIPS } from '../../services/video/query'
import { APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK } from '../../services/apollo-graphql/index'
import { SHORT_NEXT_GA4_EVENT } from '../../services/event-tracking/index'
import {
  CONTENT_IMAGE_TYPE_KEYART_WITH_TEXT,
  CONTENT_IMAGE_TYPE_KEYART_NO_TEXT,
  CONTENT_IMAGE_TYPE_POSTER,
} from '../../services/content/index'
import { getPrimary } from '../../services/languages/index'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../Loading/index'
import VerticaVideosSlider from './VerticaVideosSlider'
import VerticalVideoPlayer from './VerticalVideoPlayer'
import VerticalVideoFeedback from './VerticalVideoFeedback'
import { GA_CLIP_CONTENT_TYPE } from './utils'

const START_VIDEO_DELAY = 500

/**
 * Vertical Videos Page
 * @param {Object} props
 * @param {Immutable.Map} props.auth
 * @param {Immutable.Map} props.user
 * @param {Object} props.history
 * @param {Object} props.location
 * @param {Function} props.setEventDefault
 */
function VerticalVideosPage (props) {
  const {
    auth,
    user,
    history,
    location,
    setEventDefault,
  } = props

  const jwt = auth.get('jwt')
  const userLanguage = getPrimary(user.getIn(['data', 'language']))

  const [playlistVideos, setPlaylistVideos] = useState(List([]))
  const [showFeedback, setShowFeedback] = useState(false)
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0)

  const videoPlayers = useRef([])
  const startTimeout = useRef()
  const containerRef = useRef()

  const [getClipsData, { data, loading }] = useLazyQuery(GET_CLIPS, {
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
  })

  const handleSlideChange = (index) => {
    if (startTimeout.current) {
      clearTimeout(startTimeout.current)
    }
    let hasPlayingVideos = false
    videoPlayers.current.forEach((player) => {
      if (player && !player.paused()) {
        hasPlayingVideos = true
        player.pause()
      }
    })

    if (videoPlayers.current[index] && hasPlayingVideos) {
      startTimeout.current = setTimeout(
        () => {
          videoPlayers.current[index].play()
        },
        START_VIDEO_DELAY,
      )
    }

    setCurrentVideoIndex(index)
    const video = playlistVideos.get(index)

    if (video) {
      history.push(`${URL_SHORT_VIDEOS}/${video.get('contentId')}`)

      setEventDefault(SHORT_NEXT_GA4_EVENT.mergeDeep({
        bc_id: video.getIn(['featureMedia', 'mediaAssets', 0, 'providerKey']),
        content_id: video.get('contentId'),
        content_type: GA_CLIP_CONTENT_TYPE,
        ui_location: 'shorts',
      }))
    }
  }

  useEffect(() => {
    if (data) {
      const contentArray = [..._get(data, 'clipList.content', [])]
      const videoIdParam = _last(location.pathname.split('/'))
      if (videoIdParam) {
        const initialVideoIndex = contentArray
          .findIndex(({ contentId }) => contentId.toString() === videoIdParam)
        if (initialVideoIndex !== -1) {
          contentArray.unshift(contentArray.splice(initialVideoIndex, 1)[0])
        }
      }
      const filteredList = fromJS(contentArray).filter((video) => !!video.getIn(['featureMedia', 'mediaAssets', 0, 'videoPlayback', 'hlsUrl']))
      setPlaylistVideos(filteredList)
    }
  }, [data])

  useEffect(() => {
    getClipsData({
      variables: {
        imageType: [
          CONTENT_IMAGE_TYPE_KEYART_WITH_TEXT,
          CONTENT_IMAGE_TYPE_KEYART_NO_TEXT,
          CONTENT_IMAGE_TYPE_POSTER,
        ],
        limit: 50,
        language: [userLanguage, '_primary'],
      },
      context: {
        jwt,
      },
    })
  }, [userLanguage, jwt])

  useEffect(() => {
    const header = global.document.getElementById('header-app')

    if (!header
      || !containerRef.current
      || typeof global.ResizeObserver !== 'function') {
      return undefined
    }

    const observer = new global.ResizeObserver(
      () => {
        const { height } = header.getBoundingClientRect()
        containerRef.current.setAttribute('style', `height: calc(100dvh - ${height}px)`)
      },
      {
        root: header,
        threshold: 0,
      },
    )

    observer.observe(header)
    global.document.body.style.overflow = 'hidden'

    return () => {
      observer.disconnect()
      global.document.body.removeAttribute('style')
    }
  }, [])

  return (
    <div ref={containerRef} className="vertical-videos-page">
      {loading && !playlistVideos.size
        ? (
          <div className="vertical-videos-page__loading">
            <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
          </div>
        ) : (
          <VerticaVideosSlider
            onSlideChange={handleSlideChange}
          >
            {playlistVideos.map((video, index) => (
              <VerticalVideoPlayer
                key={video.get('contentId')}
                video={video}
                index={index}
                currentVideoIndex={currentVideoIndex}
                onFeedbackClick={() => setShowFeedback(true)}
                onCreated={(player) => {
                  videoPlayers.current[index] = player
                }}
                onDestroyed={() => {
                  videoPlayers.current[index] = null
                }}
              />
            ))}
          </VerticaVideosSlider>
        )
      }
      {showFeedback && (
        <VerticalVideoFeedback onClose={() => setShowFeedback(false)} />
      )}
    </div>
  )
}

export default compose(
  connectRedux(
    (state) => {
      return {
        user: state.user,
        auth: state.auth,
        resolver: state.resolver,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setEventDefault: actions.eventTracking.setEventDefault,
      }
    },
  ),
)(VerticalVideosPage)
